/**
 * useAuth Hook
 * Provides authentication functionality (login, logout, auth state)
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this hook only handles authentication UI logic.
 * All authentication is performed by Firebase and backend services.
 */

import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setAuth, setUser, setLoading, setError, setRememberMe, logout as logoutAction } from '../../../store/slices/authSlice';
import { getFirebaseAuth } from '../../../config/firebase';
import { mapBackendUserToUser } from '../../../types/user';
import { getErrorDetails, isAxiosErrorWithStatus } from '../../../utils/errorHandling';
import { createAndStoreSession } from '../../ai-assist/services/sessionService';

// Firebase types (will be available after installing firebase package)
type FirebaseUser = {
  uid: string;
  email: string | null;
  phoneNumber?: string | null;
  displayName?: string | null;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
};

export function useAuth() {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);

  /**
   * Login with email and password
   * Uses Firebase Auth, then stores JWT token in Redux
   * @param rememberMe - If true, stores token in localStorage; otherwise uses sessionStorage
   */
  const login = useCallback(
    async (email: string, password: string, rememberMe: boolean = false): Promise<void> => {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          throw new Error('Firebase Auth is not initialized. Please check Firebase configuration.');
        }

        // Import Firebase Auth functions dynamically
        const { signInWithEmailAndPassword } = await import('firebase/auth');

        // Sign in with Firebase
        const userCredential = await signInWithEmailAndPassword(authInstance, email, password);
        const firebaseUser = userCredential.user as unknown as FirebaseUser;

        // Get JWT token from Firebase
        const token = await firebaseUser.getIdToken();

        // Store remember me preference
        dispatch(setRememberMe(rememberMe));

        // Store auth state in Redux
        dispatch(
          setAuth({
            firebase_uid: firebaseUser.uid,
            jwt_token: token,
            rememberMe,
          })
        );

        // Fetch user data from backend User Service
        try {
          const { api } = await import('../../../api/client');
          const userResponse = await api.getCurrentUser();
          if (userResponse.data?.data) {
            const mappedUser = mapBackendUserToUser(userResponse.data.data);
            if (mappedUser) {
              dispatch(setUser(mappedUser));
            }
          }
        } catch (userError) {
          // User data fetch failed - continue with auth, user data can be fetched later
          // eslint-disable-next-line no-console
          console.error('Failed to fetch user data during login:', getErrorDetails(userError));
        }

        // Create AI Assist session after successful login
        try {
          await createAndStoreSession(rememberMe);
          // eslint-disable-next-line no-console
          console.log('[useAuth] AI Assist session created successfully after login');
        } catch (sessionError) {
          // Session creation failed - log but don't block login
          // Session can be created later when user accesses AI Assist page
          // eslint-disable-next-line no-console
          console.warn('Failed to create AI Assist session during login:', getErrorDetails(sessionError));
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';
        dispatch(setError(errorMessage));
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  /**
   * Register new user with email and password
   * Uses Firebase Auth, then stores JWT token in Redux
   */
  const register = useCallback(
    async (email: string, password: string, fullName?: string): Promise<void> => {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          throw new Error('Firebase Auth is not initialized. Please check Firebase configuration.');
        }

        // Import Firebase Auth functions dynamically
        const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');

        // Create user with Firebase
        const userCredential = await createUserWithEmailAndPassword(authInstance, email, password);
        const firebaseUser = userCredential.user;

        // Update display name if provided
        if (fullName) {
          await updateProfile(firebaseUser, { displayName: fullName });
        }

        // Get JWT token from Firebase
        const token = await firebaseUser.getIdToken();

        // Store auth state in Redux (registration always starts with rememberMe false)
        const firebaseUserTyped = firebaseUser as unknown as FirebaseUser;
        dispatch(
          setAuth({
            firebase_uid: firebaseUserTyped.uid,
            jwt_token: token,
            rememberMe: false,
          })
        );

        // Create user in backend User Service
        try {
          const { api } = await import('../../../api/client');
          // Parse full name into first_name and last_name
          const nameParts = fullName ? fullName.trim().split(/\s+/) : [];
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';

          const userResponse = await api.createUser({
            email: email,
            first_name: firstName,
            last_name: lastName,
          });
          if (userResponse.data?.data) {
            const mappedUser = mapBackendUserToUser(userResponse.data.data);
            if (mappedUser) {
              dispatch(setUser(mappedUser));
            }
          }
        } catch (userError) {
          // User creation failed - continue with auth, user can be created later
          // eslint-disable-next-line no-console
          console.warn('Failed to create user in backend:', userError);
        }

        // Create AI Assist session after successful registration
        try {
          await createAndStoreSession(false); // Registration defaults to sessionStorage
          // eslint-disable-next-line no-console
          console.log('[useAuth] AI Assist session created successfully after registration');
        } catch (sessionError) {
          // Session creation failed - log but don't block registration
          // Session can be created later when user accesses AI Assist page
          // eslint-disable-next-line no-console
          console.warn('Failed to create AI Assist session during registration:', getErrorDetails(sessionError));
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Registration failed. Please try again.';
        dispatch(setError(errorMessage));
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  /**
   * Logout user
   * Signs out from Firebase and clears Redux state
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      const authInstance = getFirebaseAuth();
      if (authInstance) {
        const { signOut } = await import('firebase/auth');
        await signOut(authInstance);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Firebase sign out error:', error);
    } finally {
      // Clear AI Assist session on logout
      try {
        const { clearStoredSessionId } = await import('../../ai-assist/services/sessionService');
        clearStoredSessionId();
      } catch (sessionError) {
        // Ignore errors when clearing session
        // eslint-disable-next-line no-console
        console.warn('Failed to clear AI Assist session on logout:', sessionError);
      }

      // Always clear Redux state even if Firebase signout fails
      dispatch(logoutAction());
    }
  }, [dispatch]);

  /**
   * Refresh JWT token
   * Gets a new token from Firebase (useful before token expiry)
   */
  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const authInstance = getFirebaseAuth();
      if (!authInstance || !authInstance.currentUser) {
        return null;
      }

      const firebaseUser = authInstance.currentUser;
      const firebaseUid = firebaseUser.uid;
      const { getIdToken } = await import('firebase/auth');
      const token = await getIdToken(firebaseUser, true); // Force refresh

      // Update token in Redux and storage
      dispatch(
        setAuth({
          firebase_uid: firebaseUid,
          jwt_token: token,
          rememberMe: auth.rememberMe,
        })
      );

      return token;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Token refresh error:', error);
      return null;
    }
  }, [dispatch, auth.rememberMe]);

  /**
   * Fetch current user data from backend
   * Useful for refreshing user data after profile updates
   */
  const fetchUserData = useCallback(async (): Promise<void> => {
    if (!auth.authenticated || !auth.firebase_uid) {
      return;
    }

    try {
      const { api } = await import('../../../api/client');
      const userResponse = await api.getCurrentUser();
      if (userResponse.data?.data) {
        const mappedUser = mapBackendUserToUser(userResponse.data.data);
        if (mappedUser) {
          dispatch(setUser(mappedUser));
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch user data:', getErrorDetails(error));

      // If it's a 500 error, log additional context
      if (isAxiosErrorWithStatus(error, 500)) {
        // eslint-disable-next-line no-console
        console.error('Server error (500) - This indicates a backend issue. Check backend logs for details.');
      }
    }
  }, [dispatch, auth.authenticated, auth.firebase_uid]);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(
    async (updates: { full_name?: string; email?: string }): Promise<void> => {
      if (!auth.firebase_uid) {
        throw new Error('User not authenticated');
      }

      try {
        const { api } = await import('../../../api/client');
        // Parse full_name into first_name and last_name for backend
        const updatePayload: { first_name?: string; last_name?: string; email?: string } = {};

        if (updates.full_name) {
          const nameParts = updates.full_name.trim().split(/\s+/);
          updatePayload.first_name = nameParts[0] || '';
          updatePayload.last_name = nameParts.slice(1).join(' ') || '';
        }

        if (updates.email) {
          updatePayload.email = updates.email;
        }

        // Use updateUser endpoint which updates the User entity (not UserProfile)
        // The backend accepts firebase_uid as user_id
        await api.updateUser(auth.firebase_uid, updatePayload as any);

        // Refresh user data after update
        await fetchUserData();
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to update profile';
        dispatch(setError(errorMessage));
        throw error;
      }
    },
    [dispatch, auth.firebase_uid, fetchUserData]
  );

  /**
   * Automatic token refresh on mount if authenticated
   */
  useEffect(() => {
    if (auth.authenticated && auth.firebase_uid) {
      // Set up automatic token refresh (every 50 minutes, tokens expire in 60 minutes)
      const refreshInterval = setInterval(async () => {
        try {
          await refreshToken();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Automatic token refresh failed:', error);
        }
      }, 50 * 60 * 1000); // 50 minutes

      return () => clearInterval(refreshInterval);
    }
  }, [auth.authenticated, auth.firebase_uid, refreshToken]);

  /**
   * Fetch user data on mount if authenticated but user data not loaded
   */
  useEffect(() => {
    if (auth.authenticated && auth.firebase_uid && !auth.user) {
      fetchUserData();
    }
  }, [auth.authenticated, auth.firebase_uid, auth.user, fetchUserData]);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(
    async (email: string): Promise<void> => {
      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          throw new Error('Firebase Auth is not initialized.');
        }

        const { sendPasswordResetEmail } = await import('firebase/auth');
        await sendPasswordResetEmail(authInstance, email);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send password reset email';
        dispatch(setError(errorMessage));
        throw error;
      }
    },
    [dispatch]
  );

  /**
   * Send OTP to mobile number
   * Returns verification ID for OTP verification
   */
  const sendMobileOTP = useCallback(
    async (phoneNumber: string): Promise<string> => {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          throw new Error('Firebase Auth is not initialized.');
        }

        const { RecaptchaVerifier, signInWithPhoneNumber } = await import('firebase/auth');

        // Format phone number (ensure it starts with +)
        const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber.replace(/\D/g, '')}`;

        // Create or get reCAPTCHA container
        let recaptchaContainer = document.getElementById('recaptcha-container');
        if (!recaptchaContainer) {
          recaptchaContainer = document.createElement('div');
          recaptchaContainer.id = 'recaptcha-container';
          recaptchaContainer.style.display = 'none';
          document.body.appendChild(recaptchaContainer);
        }

        // Create reCAPTCHA verifier
        const recaptchaVerifier = new RecaptchaVerifier(authInstance, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {
            // reCAPTCHA solved
          },
        });

        // Send OTP
        const confirmationResult = await signInWithPhoneNumber(authInstance, formattedPhone, recaptchaVerifier);

        return confirmationResult.verificationId;
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send OTP';
        dispatch(setError(errorMessage));
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  /**
   * Verify mobile OTP
   */
  const verifyMobileOTP = useCallback(
    async (verificationId: string, otp: string): Promise<void> => {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          throw new Error('Firebase Auth is not initialized.');
        }

        const { PhoneAuthProvider, signInWithCredential } = await import('firebase/auth');

        // Create credential from verification ID and OTP
        const credential = PhoneAuthProvider.credential(verificationId, otp);

        // Sign in with credential
        const userCredential = await signInWithCredential(authInstance, credential);
        const firebaseUser = userCredential.user as unknown as FirebaseUser;

        // Get JWT token
        const token = await firebaseUser.getIdToken();

        // Store auth state
        dispatch(
          setAuth({
            firebase_uid: firebaseUser.uid,
            jwt_token: token,
            rememberMe: false,
          })
        );

        // Create or fetch user in backend
        try {
          const { api } = await import('../../../api/client');

          // Try to get existing user first
          try {
            const userResponse = await api.getCurrentUser();
            if (userResponse.data?.data) {
              const mappedUser = mapBackendUserToUser(userResponse.data.data);
              if (mappedUser) {
                dispatch(setUser(mappedUser));
              }
            }
          } catch (getUserError) {
            // User doesn't exist, create new user
            // Note: Phone auth doesn't provide email, so we use empty string
            // Backend should handle phone-based users separately if needed
            const userResponse = await api.createUser({
              email: '', // Phone auth doesn't provide email
              first_name: '',
              last_name: '',
            });

            if (userResponse.data?.data) {
              const mappedUser = mapBackendUserToUser(userResponse.data.data);
              if (mappedUser) {
                dispatch(setUser(mappedUser));
              }
            }
          }
        } catch (userError) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch/create user data during mobile OTP verification:', getErrorDetails(userError));
        }

        // Create AI Assist session after successful mobile OTP verification
        try {
          await createAndStoreSession(false); // Mobile OTP defaults to sessionStorage
          // eslint-disable-next-line no-console
          console.log('[useAuth] AI Assist session created successfully after mobile OTP verification');
        } catch (sessionError) {
          // Session creation failed - log but don't block login
          // Session can be created later when user accesses AI Assist page
          // eslint-disable-next-line no-console
          console.warn('Failed to create AI Assist session during mobile OTP verification:', getErrorDetails(sessionError));
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to verify OTP';
        dispatch(setError(errorMessage));
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  /**
   * Send email verification link
   * Sends a passwordless sign-in link to email
   * User will set password when they click the link
   */
  const sendEmailVerificationLink = useCallback(
    async (email: string): Promise<void> => {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          throw new Error('Firebase Auth is not initialized.');
        }

        // Use Firebase's sendSignInLinkToEmail for passwordless authentication
        const { sendSignInLinkToEmail } = await import('firebase/auth');

        const actionCodeSettings = {
          url: `${window.location.origin}/auth/verify-email?email=${encodeURIComponent(email)}`,
          handleCodeInApp: true,
        };

        await sendSignInLinkToEmail(authInstance, email, actionCodeSettings);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send verification email';
        dispatch(setError(errorMessage));
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  /**
   * Sign in with Google
   * Uses Firebase Auth Google provider, then stores JWT token in Redux
   */
  const signInWithGoogle = useCallback(
    async (): Promise<void> => {
      dispatch(setLoading(true));
      dispatch(setError(null));

      try {
        const authInstance = getFirebaseAuth();
        if (!authInstance) {
          throw new Error('Firebase Auth is not initialized. Please check Firebase configuration.');
        }

        // Import Firebase Auth functions dynamically
        const { signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');

        // Create Google provider instance
        const provider = new GoogleAuthProvider();

        // Sign in with Google popup
        const userCredential = await signInWithPopup(authInstance, provider);
        const firebaseUser = userCredential.user as unknown as FirebaseUser;

        // Get JWT token from Firebase
        const token = await firebaseUser.getIdToken();

        // Store auth state in Redux (default to rememberMe: false for OAuth)
        dispatch(
          setAuth({
            firebase_uid: firebaseUser.uid,
            jwt_token: token,
            rememberMe: false,
          })
        );

        // Fetch or create user data from backend User Service
        try {
          const { api } = await import('../../../api/client');

          // Try to get existing user first
          try {
            const userResponse = await api.getCurrentUser();
            if (userResponse.data?.data) {
              const mappedUser = mapBackendUserToUser(userResponse.data.data);
              if (mappedUser) {
                dispatch(setUser(mappedUser));
              }
            }
          } catch (getUserError) {
            // User doesn't exist, create new user
            // Extract name from Firebase user
            const displayName = firebaseUser.email?.split('@')[0] || '';
            const nameParts = displayName.trim().split(/\s+/);
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            const userResponse = await api.createUser({
              email: firebaseUser.email || '',
              first_name: firstName,
              last_name: lastName,
            });

            if (userResponse.data?.data) {
              const mappedUser = mapBackendUserToUser(userResponse.data.data);
              if (mappedUser) {
                dispatch(setUser(mappedUser));
              }
            }
          }
        } catch (userError) {
          // User data fetch/create failed - continue with auth, user data can be fetched later
          // eslint-disable-next-line no-console
          console.error('Failed to fetch/create user data during Google sign-in:', getErrorDetails(userError));
        }

        // Create AI Assist session after successful Google sign-in
        try {
          await createAndStoreSession(false); // Google sign-in defaults to sessionStorage
          // eslint-disable-next-line no-console
          console.log('[useAuth] AI Assist session created successfully after Google sign-in');
        } catch (sessionError) {
          // Session creation failed - log but don't block login
          // Session can be created later when user accesses AI Assist page
          // eslint-disable-next-line no-console
          console.warn('Failed to create AI Assist session during Google sign-in:', getErrorDetails(sessionError));
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Google sign-in failed. Please try again.';
        dispatch(setError(errorMessage));
        throw error;
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  return {
    ...auth,
    isAdmin: auth.user?.role === 'admin',
    login,
    register,
    logout,
    refreshToken,
    fetchUserData,
    updateProfile,
    resetPassword,
    signInWithGoogle,
    sendMobileOTP,
    verifyMobileOTP,
    sendEmailVerificationLink,
  };
}

